import cx from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Icon } from '@sb/design-system';

/**
 * Two action-button kinds:
 *
 * - `confirmation`: single primary button. Tapping treats the button
 *   label as if the user had typed it into the chat input, with a
 *   short delay (default 500ms) before invoking `onConfirm`. The
 *   chat mount wires `onConfirm` to inject the label + send.
 * - `alertConfirmation`: a yellow alert card (icon + title + body)
 *   stacked on top of the primary button. Same paste-and-send
 *   semantics on tap.
 *
 * **This is not the `/confirm` HTTP flow.** Tool-use approval is
 * handled separately by the ConfirmationModal flow; these are
 * paste-and-send shortcuts only.
 */
export type ConfirmationCardProps =
  | {
      kind: 'confirmation';
      label: string;
      onConfirm: (label: string) => void;
      autoSendDelayMs?: number;
      disabled?: boolean;
    }
  | {
      kind: 'alertConfirmation';
      label: string;
      alertTitle: string;
      alertBody: string;
      onConfirm: (label: string) => void;
      autoSendDelayMs?: number;
      disabled?: boolean;
    };

const DEFAULT_AUTO_SEND_DELAY_MS = 500;

function PrototypeCTAButton({
  label,
  isPressed,
  disabled = false,
  onClick,
}: {
  label: string;
  isPressed: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  // Cursor / background / border are all driven by `isPressed` so
  // the same markup can render both the active (orange) and post-
  // press (white + checkmark) states. `disabled` is forwarded to
  // the DOM button so screen readers + keyboard focus reflect it.
  const isInactive = isPressed || disabled;

  return (
    <div
      className="tw-rounded-14 tw-overflow-hidden"
      style={{
        background: 'var(--surface-primary-elevated)',
        border: '1px solid var(--divider-primary)',
      }}
    >
      <button
        type="button"
        onClick={onClick}
        disabled={isInactive}
        aria-label={label}
        className={cx(
          'tw-flex',
          'tw-items-center',
          'tw-justify-center',
          'tw-gap-8',
          'tw-w-full',
          'tw-border-none',
          'tw-outline-none',
          'tw-text-center',
        )}
        style={{
          // 14px padding isn't in the spacing scale (12 → 16).
          padding: '14px 14px',
          background: isPressed ? 'var(--orange)' : 'transparent',
          cursor: isInactive ? 'default' : 'pointer',
          opacity: disabled && !isPressed ? 0.5 : 1,
          transition: 'all 0.2s ease',
          fontFamily: 'inherit',
        }}
      >
        <div
          className={cx('tw-font-medium', isPressed && 'tw-text-light')}
          style={{
            // 14/`-0.2` letter-spacing aren't in the typography scale.
            fontSize: 14,
            color: isPressed ? undefined : 'var(--label-primary)',
            letterSpacing: -0.2,
          }}
        >
          {label}
        </div>
        {isPressed && (
          <div
            className={cx(
              'tw-flex',
              'tw-items-center',
              'tw-justify-center',
              'tw-rounded-full',
              'tw-shrink-0',
              'tw-bg-light',
            )}
            style={{
              // 18×18 isn't in the spacing scale (16 → 20).
              width: 18,
              height: 18,
            }}
          >
            <svg
              width={10}
              height={10}
              viewBox="0 0 12 12"
              style={{ fill: 'var(--orange)' }}
            >
              <path d="M10.28 2.72a.75.75 0 0 1 0 1.06l-5.25 5.25a.75.75 0 0 1-1.06 0L1.72 6.78a.75.75 0 0 1 1.06-1.06L4.75 7.69l4.72-4.72a.75.75 0 0 1 1.06-.25l-.25.25.25-.25Z" />
            </svg>
          </div>
        )}
      </button>
    </div>
  );
}

export function ConfirmationCard(props: ConfirmationCardProps) {
  const {
    kind,
    label,
    onConfirm,
    autoSendDelayMs = DEFAULT_AUTO_SEND_DELAY_MS,
    disabled,
  } = props;

  const [isPressed, setIsPressed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const handleClick = useCallback(() => {
    if (disabled || isPressed) return;

    setIsPressed(true);

    // Pause briefly so the orange + checkmark state is visible
    // before the auto-send fires. `isPressed` stays true after the
    // timer so the card remains in its "acknowledged" state instead
    // of flickering back to the un-pressed look.
    timerRef.current = setTimeout(() => {
      timerRef.current = undefined;
      onConfirm(label);
    }, autoSendDelayMs);
  }, [disabled, isPressed, label, onConfirm, autoSendDelayMs]);

  if (kind === 'alertConfirmation') {
    const { alertTitle, alertBody } = props;

    return (
      // 14px top margin isn't in the spacing scale (12 → 16).
      <div style={{ marginTop: 14 }}>
        <div className="tw-flex tw-items-start tw-gap-12" role="status">
          <div
            className={cx(
              'tw-flex',
              'tw-items-center',
              'tw-justify-center',
              'tw-shrink-0',
              'tw-w-32',
              'tw-h-32',
              'tw-rounded-full',
            )}
            style={{
              background: 'rgba(234, 179, 8, 0.15)',
            }}
          >
            <Icon
              kind="exclamationTriangle"
              // 18×18 isn't in the icon scale; the warning glyph uses
              // the themeable `--mango` token.
              style={{ width: 18, height: 18, color: 'var(--mango)' }}
            />
          </div>
          <div className="tw-flex-1 tw-min-w-0">
            <div
              style={{
                // 14/600 + `-0.2` letter-spacing aren't in the
                // typography scale (size 13 → 15, weights 500 → 700).
                fontSize: 14,
                fontWeight: 600,
                color: 'var(--label-primary)',
                letterSpacing: -0.2,
              }}
            >
              {alertTitle}
            </div>
            <div
              className="tw-text-13 tw-mt-2"
              style={{
                // 1.45 line-height isn't a Tailwind preset; 55%-black
                // body text doesn't have a token at this weight.
                color: 'var(--label-secondary)',
                lineHeight: 1.45,
              }}
            >
              {alertBody}
            </div>
          </div>
        </div>
        <div className="tw-mt-12">
          <PrototypeCTAButton
            label={label}
            isPressed={isPressed}
            disabled={disabled}
            onClick={handleClick}
          />
        </div>
      </div>
    );
  }

  return (
    // 14px top margin isn't in the spacing scale (12 → 16).
    <div style={{ marginTop: 14 }}>
      <PrototypeCTAButton
        label={label}
        isPressed={isPressed}
        disabled={disabled}
        onClick={handleClick}
      />
    </div>
  );
}
